import { useState, useEffect, useCallback } from "react";
import { useInstantSearch } from "react-instantsearch";

// Define the type for the `onChange` callback
type OnChangeHandler = (newIndex: string, prevIndex: string) => void;

// Hook return type
interface UseIndexSelectionResult {
    selectedIndex: string;
    selectionHandler: (indexName: string) => void;
    getHitsCount: (indexName: string) => number | "?";
}

/**
 * Custom hook to manage index selection logic.
 * 
 * @param defaultSelectedIndex - The default selected index.
 * @param onChange - Callback function triggered when selection changes.
 * @returns Hook with selected index, selection handler, and hit count retriever.
 */
export function useIndexSelection(
    defaultSelectedIndex: string,
    onChange: OnChangeHandler
): UseIndexSelectionResult {
    const [selectedIndex, setSelectedIndex] = useState<string>(defaultSelectedIndex);
    const { scopedResults } = useInstantSearch();

    // Sync state when `defaultSelectedIndex` changes
    useEffect(() => {
        setSelectedIndex(defaultSelectedIndex);
    }, [defaultSelectedIndex]);

    // Selection handler
    const selectionHandler = useCallback(
        (indexName: string) => {
            if (selectedIndex !== indexName) {
                setSelectedIndex(indexName);
                onChange(indexName, selectedIndex);
            }
        },
        [selectedIndex, onChange]
    );

    // Get hit count for an index
    const getHitsCount = (indexName: string): number | "?" => {
        return scopedResults.find((result) => result.indexId === indexName)?.results?.nbHits ?? "?";
    };

    return { selectedIndex, selectionHandler, getHitsCount };
}
